import { Box, BoxProps } from "@aircall/tractor";
import {
  PropsWithChildren,
  UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

function isScrollAtBottom<T extends HTMLElement>(element: T): boolean {
  return element.scrollHeight <= element.scrollTop + element.clientHeight;
}

function isScrollAtTop<T extends HTMLElement>(element: T): boolean {
  return element.scrollTop === 0;
}

type ScrollShadowContainerProps = PropsWithChildren<BoxProps>;

export function ScrollShadowContainer({
  children,
  ...boxProps
}: ScrollShadowContainerProps) {
  const localRef = useRef<HTMLDivElement>(null);
  const [hasBottomShadow, setBottomShadow] = useState<boolean>(false);
  const [hasTopShadow, setTopShadow] = useState<boolean>(false);

  const setScrollShadow = useCallback((el: HTMLDivElement) => {
    const atBottom = isScrollAtBottom<HTMLDivElement>(el);
    const atTop = isScrollAtTop<HTMLDivElement>(el);

    setBottomShadow(!atBottom);
    setTopShadow(!atTop);
  }, []);

  useEffect(() => {
    if (localRef.current) {
      setScrollShadow(localRef.current);
    }
  }, [setScrollShadow]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      setScrollShadow(event.currentTarget);
    },
    [setScrollShadow]
  );

  const shadows: string[] = [
    ...(hasTopShadow ? [`inset 0 19px 20px -31px grey`] : []),
    ...(hasBottomShadow ? [`inset 0 -19px 20px -31px grey`] : []),
  ];

  const boxShadow = shadows.length > 0 ? shadows.join(",") : undefined;

  return (
    <Box
      {...boxProps}
      ref={localRef}
      onScroll={handleScroll}
      style={{
        boxShadow,
        transition: "500ms box-shadow",
      }}
    >
      {children}
    </Box>
  );
}
